import { useEffect, useState, useRef } from 'react';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import RepertoireSection from './components/RepertoireSection';
import VideosSection from './components/VideosSection';
import VenturesSection from './components/VenturesSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import SeoHead from '../../components/feature/SeoHead';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);

  // Bernstein quote visibility
  const quoteRef = useRef<HTMLDivElement>(null);
  const [quoteVisible, setQuoteVisible] = useState(false);

  // 帷幕控制：Bernstein 动画结束后立即开启
  const videosSectionRef = useRef<HTMLDivElement>(null);
  const [curtainOpen, setCurtainOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 检查是否从其他页面跳转过来需要滚动到某个锚点
  useEffect(() => {
    const target = sessionStorage.getItem('scrollToSection');
    if (target) {
      sessionStorage.removeItem('scrollToSection');
      // 稍作延迟确保页面渲染完成
      const timer = setTimeout(() => {
        const element = document.getElementById(target);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setMenuVisible(true));
      });
    } else {
      setMenuVisible(false);
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setQuoteVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    if (quoteRef.current) observer.observe(quoteRef.current);
    return () => observer.disconnect();
  }, []);

  // Bernstein 动画总时长约 2.2s（最后一个元素 delay 1.8s + duration 0.4s），动画结束后立即开帷幕
  useEffect(() => {
    if (!quoteVisible) return;
    const timer = setTimeout(() => setCurtainOpen(true), 2200);
    return () => clearTimeout(timer);
  }, [quoteVisible]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const menuItems = [
    { label: 'About', action: () => scrollToSection('about') },
    { label: 'Repertoire', action: () => scrollToSection('repertoire') },
    { label: 'Videos', action: () => scrollToSection('videos') },
    { label: 'Résumé', href: '/resume' },
    { label: 'Photo Gallery', href: '/gallery' },
    { label: 'Contact', action: () => scrollToSection('contact') },
  ];

  const homeJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Wei Chen',
      jobTitle: 'Baritone',
      description: 'New York classical baritone performing leading opera roles at Carnegie Hall and major international stages. Specialist in Mozart, Verdi, and Puccini.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/`,
      image: 'https://public.readdy.ai/ai/img_res/56c79e41-d967-4e72-aa45-446ef434d0a7.png',
      email: 'weichenvictor@gmail.com',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'New York',
        addressRegion: 'NY',
        addressCountry: 'US',
      },
      alumniOf: [
        { '@type': 'EducationalOrganization', name: 'Mannes School of Music, The New School' },
        { '@type': 'EducationalOrganization', name: 'Manhattan School of Music' },
        { '@type': 'EducationalOrganization', name: 'Xinghai Conservatory of Music' },
      ],
      knowsAbout: ['Opera', 'Baritone', 'Mozart', 'Verdi', 'Puccini', 'Baroque Music', 'Classical Vocal Performance'],
      sameAs: [],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Wei Chen – Classical Baritone',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/`,
      description: 'Official website of Wei Chen, New York baritone and classical vocal artist.',
    },
  ];

  return (
    <div className="bg-opera-black text-white relative">
      <SeoHead
        title="Wei Chen – New York Baritone | Opera Singer | Classical Vocal Artist"
        description="Wei Chen is a distinguished New York baritone performing leading opera roles at Carnegie Hall and major international stages. Specialist in Mozart, Verdi, and Puccini."
        keywords="Wei Chen, Baritone, Opera Singer, New York, Classical Vocal Artist, Mozart, Verdi, Puccini, Carnegie Hall"
        canonical="/"
        jsonLd={homeJsonLd}
      />
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || mobileMenuOpen ? 'bg-deep-black/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <img
              src="https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/1fde6b17644309384f7e4bfbfdddec92.png"
              alt="Wei Chen Badge"
              className="h-12 w-12 md:h-16 md:w-16 object-contain drop-shadow-lg"
            />
            <h1 className="text-lg md:text-2xl font-cinzel tracking-widest" style={{ color: '#d4891a', textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.6)' }}>WEI CHEN</h1>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex flex-col items-center justify-center gap-0">
            {/* 上方贯穿线 */}
            <div className="w-full h-px mb-2" style={{ background: 'rgba(212,137,26,0.35)' }} />

            {/* 菜单项行 */}
            <div className="flex items-center gap-8">
              {['about','repertoire','videos'].map((id) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className="font-cormorant text-lg font-semibold transition-colors duration-300 relative group whitespace-nowrap cursor-pointer capitalize"
                  style={{ color: '#d4891a', textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.5)' }}
                >
                  {id.charAt(0).toUpperCase() + id.slice(1)}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-green group-hover:w-full transition-all duration-300"></span>
                </button>
              ))}
              <a
                href="/resume"
                className="font-cormorant text-lg font-semibold transition-colors duration-300 relative group whitespace-nowrap cursor-pointer"
                style={{ color: '#d4891a', textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.5)' }}
              >
                Résumé
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-green group-hover:w-full transition-all duration-300"></span>
              </a>
              <a
                href="/gallery"
                className="font-cormorant text-lg font-semibold transition-colors duration-300 relative group whitespace-nowrap cursor-pointer"
                style={{ color: '#d4891a', textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.5)' }}
              >
                Photo Gallery
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-green group-hover:w-full transition-all duration-300"></span>
              </a>
              <button
                onClick={() => scrollToSection('contact')}
                className="font-cormorant text-lg font-semibold transition-colors duration-300 relative group whitespace-nowrap cursor-pointer"
                style={{ color: '#d4891a', textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.5)' }}
              >
                Contact
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-green group-hover:w-full transition-all duration-300"></span>
              </button>
            </div>

            {/* 下方贯穿线 */}
            <div className="w-full h-px mt-2" style={{ background: 'rgba(212,137,26,0.35)' }} />
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 cursor-pointer relative"
            aria-label="Toggle menu"
          >
            <span
              className="block h-0.5 w-6 rounded-full transition-all duration-300 origin-center"
              style={{
                backgroundColor: '#d4891a',
                transform: mobileMenuOpen ? 'translateY(8px) rotate(45deg)' : 'none',
                boxShadow: '0 1px 4px rgba(0,0,0,0.6)',
              }}
            />
            <span
              className="block h-0.5 w-6 rounded-full transition-all duration-300"
              style={{
                backgroundColor: '#d4891a',
                opacity: mobileMenuOpen ? 0 : 1,
                transform: mobileMenuOpen ? 'scaleX(0)' : 'scaleX(1)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.6)',
              }}
            />
            <span
              className="block h-0.5 w-6 rounded-full transition-all duration-300 origin-center"
              style={{
                backgroundColor: '#d4891a',
                transform: mobileMenuOpen ? 'translateY(-8px) rotate(-45deg)' : 'none',
                boxShadow: '0 1px 4px rgba(0,0,0,0.6)',
              }}
            />
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        <div
          className="md:hidden overflow-hidden transition-all duration-500 ease-in-out"
          style={{
            maxHeight: mobileMenuOpen ? '400px' : '0px',
            opacity: menuVisible ? 1 : 0,
          }}
        >
          <div className="relative overflow-hidden" style={{
            background: 'linear-gradient(160deg, #0d0b07 0%, #111008 40%, #0a0d0a 100%)',
            borderTop: '1px solid rgba(201,168,76,0.25)',
          }}>
            {/* 顶部金色细线装饰 */}
            <div className="absolute top-0 left-6 right-6 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(201,168,76,0.6), transparent)' }} />

            {/* 低音谱号装饰背景 */}
            <div
              className="absolute right-0 top-0 bottom-0 flex items-center justify-end pointer-events-none select-none overflow-hidden"
              style={{ width: '62%' }}
            >
              <span
                style={{
                  fontFamily: '"Didot", "Bodoni MT", "Playfair Display", "IM Fell English", "Libre Baskerville", Georgia, serif',
                  fontSize: '22rem',
                  lineHeight: 0.85,
                  userSelect: 'none',
                  display: 'block',
                  transform: 'translateX(8%) skewX(-6deg) skewY(1.5deg) scaleY(1.12)',
                  background: 'linear-gradient(125deg, #1a0e00 0%, #5a3a00 12%, #a07020 24%, #d4a84c 36%, #ffe8a0 46%, #c9891a 56%, #7a5000 68%, #3a2000 78%, #b8943a 88%, #ffe0a0 96%, #c9a84c 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  opacity: 0.18,
                  filter: 'drop-shadow(3px 6px 8px rgba(0,0,0,0.95)) drop-shadow(-2px -3px 4px rgba(255,210,80,0.18)) drop-shadow(6px 12px 18px rgba(0,0,0,0.85)) drop-shadow(1px 2px 2px rgba(180,120,0,0.3))',
                  fontWeight: 400,
                  fontStyle: 'normal',
                }}
              >
                𝄢
              </span>
            </div>

            {/* 菜单项 */}
            <div className="relative z-10 px-6 py-4">
              {menuItems.map((item, index) => {
                const isLast = index === menuItems.length - 1;
                const itemContent = (
                  <>
                    {/* 标签文字 */}
                    <span
                      className="font-cinzel tracking-widest text-sm"
                      style={{ color: '#e8d4a0', letterSpacing: '0.14em' }}
                    >
                      {item.label}
                    </span>
                  </>
                );

                const sharedStyle: React.CSSProperties = {
                  transitionDelay: menuVisible ? `${index * 55}ms` : '0ms',
                  transform: menuVisible ? 'translateX(0)' : 'translateX(-20px)',
                  opacity: menuVisible ? 1 : 0,
                  transition: 'transform 0.45s cubic-bezier(0.22,1,0.36,1), opacity 0.45s ease',
                };

                const sharedClass = `flex items-center w-full py-3.5 cursor-pointer group`;

                return (
                  <div key={item.label}>
                    {item.href ? (
                      <a href={item.href} className={sharedClass} style={sharedStyle}>
                        {itemContent}
                      </a>
                    ) : (
                      <button onClick={item.action} className={sharedClass} style={sharedStyle}>
                        {itemContent}
                      </button>
                    )}
                    {!isLast && (
                      <div
                        className="w-full"
                        style={{
                          height: '1px',
                          background: 'linear-gradient(to right, rgba(201,168,76,0.25), rgba(201,168,76,0.08), transparent)',
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* 底部金色细线装饰 */}
            <div className="absolute bottom-0 left-6 right-6 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(201,168,76,0.4), transparent)' }} />
          </div>
        </div>
      </nav>

      <HeroSection />
      <AboutSection />
      <RepertoireSection />

      {/* Bernstein Quote Interlude */}
      <div
        ref={quoteRef}
        className="relative py-16 md:py-28 px-4 md:px-6 overflow-hidden"
        style={{ background: 'linear-gradient(to bottom, #0a0a0a, #0f0e0b, #0a0a0a)' }}
      >
        <style>{`
          @keyframes quoteLineExpand {
            from { transform: scaleX(0); opacity: 0; }
            to   { transform: scaleX(1); opacity: 1; }
          }
          @keyframes quoteFadeUp {
            from { opacity: 0; transform: translateY(36px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes quoteMarkPop {
            0%   { opacity: 0; transform: scale(0.6) translateY(8px); }
            60%  { opacity: 1; transform: scale(1.08) translateY(-2px); }
            100% { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes quoteGlowPulse {
            0%, 100% { opacity: 0.07; }
            50%       { opacity: 0.14; }
          }
        `}</style>

        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(122,171,130,0.07) 0%, transparent 70%)',
            animation: quoteVisible ? 'quoteGlowPulse 4s ease-in-out infinite' : 'none',
          }}
        />

        <div className="flex items-center justify-center gap-3 md:gap-4 mb-8 md:mb-12">
          <div
            className="h-px flex-1 max-w-xs"
            style={{
              background: 'linear-gradient(to right, transparent, #7aab82)',
              animation: quoteVisible ? 'quoteLineExpand 0.6s cubic-bezier(0.4,0,0.2,1) forwards' : 'none',
              opacity: quoteVisible ? undefined : 0,
              transformOrigin: 'left center',
            }}
          />
          <i className="ri-music-fill text-xs" style={{ color: '#7aab82', opacity: quoteVisible ? 0.8 : 0, transition: quoteVisible ? 'opacity 0.4s ease 0.4s' : 'none' }}></i>
          <div
            className="h-px flex-1 max-w-xs"
            style={{
              background: 'linear-gradient(to left, transparent, #7aab82)',
              animation: quoteVisible ? 'quoteLineExpand 0.6s cubic-bezier(0.4,0,0.2,1) forwards' : 'none',
              opacity: quoteVisible ? undefined : 0,
              transformOrigin: 'right center',
            }}
          />
        </div>

        <div className="max-w-4xl mx-auto text-center px-2">
          <span style={{ display: 'inline-block', color: '#7aab82', fontSize: 'clamp(2.5rem, 8vw, 4rem)', lineHeight: 0, verticalAlign: '-0.4em', marginRight: '0.1em', fontFamily: 'Georgia, serif', animation: quoteVisible ? 'quoteMarkPop 0.8s cubic-bezier(0.34,1.56,0.64,1) 0.3s both' : 'none', opacity: quoteVisible ? undefined : 0 }}>"</span>
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: 'clamp(1.3rem, 4.5vw, 2.4rem)', color: '#ede8d8', letterSpacing: '0.01em', textShadow: '0 2px 20px rgba(0,0,0,0.8)', display: 'inline', animation: quoteVisible ? 'quoteFadeUp 1.2s cubic-bezier(0.22,1,0.36,1) 0.6s both' : 'none', opacity: quoteVisible ? undefined : 0 }}>
            Music can name the unnameable and communicate the unknowable.
          </span>
          <span style={{ display: 'inline-block', color: '#7aab82', fontSize: 'clamp(2.5rem, 8vw, 4rem)', lineHeight: 0, verticalAlign: '-0.4em', marginLeft: '0.1em', fontFamily: 'Georgia, serif', animation: quoteVisible ? 'quoteMarkPop 0.8s cubic-bezier(0.34,1.56,0.64,1) 1.3s both' : 'none', opacity: quoteVisible ? undefined : 0 }}>"</span>

          <div style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', animation: quoteVisible ? 'quoteFadeUp 1s cubic-bezier(0.22,1,0.36,1) 1.6s both' : 'none', opacity: quoteVisible ? undefined : 0 }}>
            <div className="h-px w-8 md:w-10" style={{ background: '#7aab82', opacity: 0.6 }} />
            <span className="font-cormorant-sc tracking-widest text-base md:text-xl uppercase" style={{ color: '#7aab82', opacity: 0.95, letterSpacing: '0.12em' }}>Leonard Bernstein</span>
            <div className="h-px w-8 md:w-10" style={{ background: '#7aab82', opacity: 0.6 }} />
          </div>
        </div>

        <div className="flex items-center justify-center gap-3 md:gap-4 mt-8 md:mt-12">
          <div className="h-px flex-1 max-w-xs" style={{ background: 'linear-gradient(to right, transparent, #7aab82)', animation: quoteVisible ? 'quoteLineExpand 0.6s cubic-bezier(0.4,0,0.2,1) 1.4s both' : 'none', opacity: quoteVisible ? undefined : 0, transformOrigin: 'left center' }} />
          <i className="ri-music-fill text-xs" style={{ color: '#7aab82', opacity: quoteVisible ? 0.8 : 0, transition: quoteVisible ? 'opacity 0.4s ease 1.8s' : 'none' }}></i>
          <div className="h-px flex-1 max-w-xs" style={{ background: 'linear-gradient(to left, transparent, #7aab82)', animation: quoteVisible ? 'quoteLineExpand 0.6s cubic-bezier(0.4,0,0.2,1) 1.4s both' : 'none', opacity: quoteVisible ? undefined : 0, transformOrigin: 'right center' }} />
        </div>
      </div>

      {/* On Stage 区域包装器，用于监听帷幕触发时机 */}
      <div ref={videosSectionRef}>
        <VideosSection curtainOpen={curtainOpen} />
      </div>
      <VenturesSection />
      <ContactSection />
      <Footer />
    </div>
  );
}